import React, { useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Col, List, Row, Select, Table, DatePicker } from "antd";
import { SearchOutlined, ClearOutlined, DownloadOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, isEmpty, get, toLower, split, last } from "lodash";

import Button from "../../components/functional_components/Button/PFButton";
import User from "../../services/api/User";
import ExpenseRefund from "../../services/api/ExpenseRefund";
import { buildQueryString, exportFile, formatNumber, showError, showSuccess } from "../../utils/utilities";
import { fileFormatXextension, langDate, paginationDefaults, dbDate } from "../../utils/properties";
import dayjs from "dayjs";
import { setLoading } from "../../redux/ducks/Loading";
import { setFilters } from "../../redux/ducks/Filters";

const ExportDocuments = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];

  const [filterLoading] = useState(false);
  const [massiveExportLoading, setMassiveExportLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [findUsers, setFindUsers] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: paginationDefaults.page,
    size: paginationDefaults.size,
    totalItems: paginationDefaults.totalItems,
  });

  const cellStyle = {
    style: { maxWidth: 250, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word" },
  };

  const renderApprovalDate = (record) => {
    let date;
    switch (record.status) {
      case "WAITING_APPROVAL_2":
        date = record.dateApproval1;
        break;
      case "WAITING_APPROVAL_3":
        date = record.dateApproval2;
        break;
      case "APPROVED":
        date = record.dateApproval3;
        break;
      default:
        date = null;
    }
    return date ? dayjs(date).format(langDate[toLower(currentLanguage)]) : "-";
  };

  const columns = [
    {
      title: t("expenseReport.filter.name"),
      dataIndex: ["expenseRefundFolder", "user"],
      key: "user",
      onCell: () => cellStyle,
      fixed: "left",
      render: (user) => {
        return user.name;
      },
    },
    {
      title: t("expenseReport.filter.surname"),
      dataIndex: ["expenseRefundFolder", "user"],
      key: "user",
      onCell: () => cellStyle,
      fixed: "left",
      render: (user) => {
        return user.surname;
      },
    },
    {
      title: t("expenseRefundFolder.table.docDate"),
      key: "date",
      onCell: () => cellStyle,
      render: (record) => {
        const date = record.type === "FLAT_RATE" ? record.departureDate : record.docDate;
        return date ? dayjs(date).format(langDate[toLower(currentLanguage)]) : "-";
      },
    },
    {
      title: t("expenseReport.modal.folderName"),
      dataIndex: ["expenseRefundFolder", "note"],
      key: "expenseRefundFolder-note",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("expenseRefundFolder.exportReport.type"),
      dataIndex: "type",
      key: "type",
      onCell: () => cellStyle,
      render: (text) => (text ? t(`expenseRefundFolder.exportReport.optionsType.${text.toLowerCase()}`) : "-"),
    },
    {
      title: t("expenseRefundFolder.exportReport.status"),
      dataIndex: "status",
      key: "status",
      onCell: () => cellStyle,
      render: (text) => (text ? t(`projects.status.${text.toLowerCase()}`) : "-"),
    },
    {
      title: t("expenseRefundFolder.table.approvalDate"),
      key: "approvalDate",
      onCell: () => cellStyle,
      render: (record) => renderApprovalDate(record),
    },
    {
      title: t("expenseRefundFolder.table.calcRefund"),
      dataIndex: "calcRefund",
      key: "calcRefund",
      onCell: () => cellStyle,
      render: (amount) => (amount ? formatNumber({ value: amount, lang: currentLanguage, suffix: "EUR" }) : "-"),
    },
    {
      title: t("expenseRefundFolder.note"),
      dataIndex: ["expenseAccountType", "description"],
      key: "account-description",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("expenseRefundFolder.table.folderNote"),
      dataIndex: ["note"],
      key: "expenseRefundFolder-note",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("expenseRefundFolder.table.folderId"),
      dataIndex: ["expenseRefundFolder", "id"],
      key: "expenseRefundFolder-folderId",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("expenseRefundFolder.table.noteId"),
      dataIndex: "id",
      key: "noteId",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("expenseRefundFolder.table.attachmentId1"),
      dataIndex: ["documents", 0, "id"],
      key: "documents[0].id",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("expenseRefundFolder.table.attachmentId2"),
      dataIndex: ["documents", 1, "id"],
      key: "documents[1].id",
      onCell: () => cellStyle,
      render: (text) => (text ? text : "-"),
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      render: (item) => (
        <Button
          currentIcon={<DownloadOutlined />}
          isDisabled={isEmpty(item.documents)}
          clickCallback={() =>
            !isEmpty(item.documents)
              ? downloadDocuments(
                  item.documents.map((doc) => ({
                    id: doc.id,
                    name: item.expenseRefundFolder.user.name,
                    surname: item.expenseRefundFolder.user.surname,
                    date: dayjs(item.docDate).format("YYYYMMDD"),
                    path: doc.filePath,
                    folderId: item.expenseRefundFolder.id,
                    noteId: item.id,
                    type: doc.type,
                    // note: item.note,
                    // expenseRefundNote: item.expenseRefundFolder.note,
                  }))
                )
              : null
          }
        />
      ),
    },
  ];

  const getFilterFromFormValues = (values) => {
    let dataFilter = cloneDeep(values);
    if (dataFilter.dateApprovalFrom) {
      dataFilter.dateApprovalFrom = dayjs(dataFilter.dateApprovalFrom, dateFormat).format(dbDate);
    } else {
      delete dataFilter.dateApprovalFrom;
    }
    if (dataFilter.dateApprovalTo) {
      dataFilter.dateApprovalTo = dayjs(dataFilter.dateApprovalTo, dateFormat).format(dbDate);
    } else {
      delete dataFilter.dateApprovalTo;
    }
    if (dataFilter.docDateFrom) {
      dataFilter.docDateFrom = dayjs(dataFilter.docDateFrom, dateFormat).format(dbDate);
    } else {
      delete dataFilter.docDateFrom;
    }
    if (dataFilter.docDateTo) {
      dataFilter.docDateTo = dayjs(dataFilter.docDateTo, dateFormat).format(dbDate);
    } else {
      delete dataFilter.docDateTo;
    }
    if (isEmpty(dataFilter.status)) {
      delete dataFilter.status;
    }
    if (isEmpty(dataFilter.types)) {
      delete dataFilter.types;
    }
    if (!dataFilter.username) {
      delete dataFilter.username;
    }
    return dataFilter;
  };

  const formikFilter = useFormik({
    initialValues: {
      dateApprovalFrom: null,
      dateApprovalTo: null,
      docDateFrom: null,
      docDateTo: null,
      status: [],
      types: [],
      username: null,
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (values.dateApprovalFrom && values.dateApprovalTo && values.dateApprovalFrom > values.dateApprovalTo) {
        errors.dateApprovalFrom = "Date approval from must be before date approval to";
        errors.dateApprovalTo = "Date approval from must be before date approval to";
      }
      if (values.docDateFrom && values.docDateTo && values.docDateFrom > values.docDateTo) {
        errors.docDateFrom = "Doc date from must be before doc date to";
        errors.docDateTo = "Doc date from must be before doc date to";
      }
      return errors;
    },
    onSubmit: async (values) => {
      const dataFilter = getFilterFromFormValues(values);
      const queryString = buildQueryString(dataFilter, {});
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  async function onSearchusers(value) {
    if (value.length >= 2) {
      const { error, data } = await User.getUsersInternalHrList(`allPages=true&surname=${value}`);
      if (!error && data) {
        setFindUsers(data.map((u) => ({ value: u.username, label: `${u.name} ${u.surname}` })));
      }
    }
  }

  async function dataList(queryString) {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await ExpenseRefund.searchDocuments(queryString);
    if (!error && data) {
      setTableData(data);
      setPagination({
        currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
        size: pageSize || paginationDefaults.size,
        totalItems: totalItems || paginationDefaults.totalItems,
      });
    }
    dispatch(setLoading(false));
  }

  const downloadDocuments = async (docIds) => {
    try {
      for (const docId of docIds) {
        const file = await ExpenseRefund.downloadDocument(docId.id);
        if (!file.error) {
          const parts = split(docId.path, "/");
          const type =
            docId.type.toLowerCase() === "receipt" ? "R" : docId.type.toLowerCase() === "payment_tracking" ? "T" : "";
          const fileName = `${docId.name} ${docId.surname}_${docId.date}_${docId.folderId}_${docId.noteId}_${type}.${last(parts).split(".")[1]}`;
          const blob = new Blob([file]);
          exportFile(blob, fileName);
        } else {
          showError(get(file, "error.messageCode", "ER000"));
        }
      }
    } catch (error) {
      showError("ER000");
    }
  };

  const handleMassiveExport = async () => {
    setMassiveExportLoading(true);

    try {
      const filterParams = getFilterFromFormValues(formikFilter.values);

      const { error } = await ExpenseRefund.massiveExportDocuments(filterParams);
      if (!error) {
        showSuccess(t("expenseRefundFolder.exportReport.massiveExportSuccess", { count: tableData.length }));
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
    } catch (error) {
      showError("ER000");
    } finally {
      setMassiveExportLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloadLoading(true);

    try {
      const filterParams = getFilterFromFormValues(formikFilter.values);
      const queryString = buildQueryString(filterParams, {});
      const file = await ExpenseRefund.getExpenseRefundExportDocuments(queryString.string);

      if (!file.error) {
        const downloadType = fileFormatXextension["xlsx"];
        const blob = new Blob([file], { type: downloadType });
        exportFile(blob, "report.xlsx");
        showSuccess();
      } else {
        showError(get(file, "error.messageCode", "ER000"));
      }
    } catch (error) {
      showError("ER000");
    } finally {
      setDownloadLoading(false);
    }
  };

  const resetFilter = () => {
    formikFilter.resetForm();
    setTableData([]);
    setFindUsers([]);
    setPagination({
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    });
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
  };

  return (
    <>
      <div className="filter-container">
        <Row gutter={[48, 32]} align="bottom">
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">{t("expenseRefundFolder.exportReport.dateApprovalFrom")}</label>
            </Row>
            <Row>
              <DatePicker
                id="dateApprovalFrom"
                name="dateApprovalFrom"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.dateApprovalFrom}
                onChange={(date) => formikFilter.setFieldValue("dateApprovalFrom", date)}
                onBlur={() => formikFilter.validateForm()}
                className={formikFilter.errors.dateApprovalFrom !== undefined && "input-error"}
              />
            </Row>
          </Col>
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">{t("expenseRefundFolder.exportReport.dateApprovalTo")}</label>
            </Row>
            <Row>
              <DatePicker
                id="dateApprovalTo"
                name="dateApprovalTo"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.dateApprovalTo}
                onChange={(date) => formikFilter.setFieldValue("dateApprovalTo", date)}
                onBlur={() => formikFilter.validateForm()}
                className={formikFilter.errors.dateApprovalTo !== undefined && "input-error"}
              />
            </Row>
          </Col>
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">
                {t("expenseRefundFolder.exportReport.user") + " " + t("expenseRefundFolder.exportReport.userLabelInfo")}
              </label>
            </Row>
            <Row>
              <Select
                showSearch
                className="w-100"
                id="username"
                name="username"
                placeholder={t("fuelCard.table.user")}
                onChange={(s) => formikFilter.setFieldValue("username", s)}
                onBlur={formikFilter.handleBlur("username")}
                value={formikFilter.values.username}
                onSearch={onSearchusers}
                options={findUsers}
              />
            </Row>
          </Col>
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">{t("expenseRefundFolder.exportReport.docDateFrom")}</label>
            </Row>
            <Row>
              <DatePicker
                id="docDateFrom"
                name="docDateFrom"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.docDateFrom}
                onChange={(date) => formikFilter.setFieldValue("docDateFrom", date)}
                onBlur={() => formikFilter.validateForm()}
                className={formikFilter.errors.docDateFrom !== undefined && "input-error"}
              />
            </Row>
          </Col>
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">{t("expenseRefundFolder.exportReport.docDateTo")}</label>
            </Row>
            <Row>
              <DatePicker
                id="docDateTo"
                name="docDateTo"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.docDateTo}
                onChange={(date) => formikFilter.setFieldValue("docDateTo", date)}
                onBlur={() => formikFilter.validateForm()}
                className={formikFilter.errors.docDateTo !== undefined && "input-error"}
              />
            </Row>
          </Col>
          {/*force status to go to next line */}
          <Col span={8}></Col>
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">{t("expenseRefundFolder.exportReport.status")}</label>
            </Row>
            <Row>
              <Select
                mode="multiple"
                className="w-100"
                id="status"
                name="status"
                placeholder={t("expenseRefundFolder.exportReport.status")}
                onChange={(s) => formikFilter.setFieldValue("status", s)}
                onBlur={formikFilter.handleBlur}
                value={formikFilter.values.status}
                options={[
                  { value: "APPROVED", label: t("projects.status.approved") },
                  { value: "PLANNING", label: t("projects.status.planning") },
                  { value: "REFUTED", label: t("projects.status.refuted") },
                  { value: "WAITING_APPROVAL_1", label: t("projects.status.waiting_approval_1") },
                  { value: "WAITING_APPROVAL_2", label: t("projects.status.waiting_approval_2") },
                  { value: "WAITING_APPROVAL_3", label: t("projects.status.waiting_approval_3") },
                ]}
              />
            </Row>
          </Col>
          <Col span={8}>
            <Row>
              <label className="fw-bold mt-10">{t("expenseRefundFolder.exportReport.type")}</label>
            </Row>
            <Row>
              <Select
                mode="multiple"
                className="w-100"
                id="types"
                name="types"
                placeholder={t("expenseRefundFolder.exportReport.type")}
                onChange={(s) => formikFilter.setFieldValue("types", s)}
                onBlur={formikFilter.handleBlur}
                value={formikFilter.values.types}
                options={[
                  { value: "JUSTIFICATION", label: t("expenseRefundFolder.exportReport.optionsType.justification") },
                  { value: "FLAT_RATE", label: t("expenseRefundFolder.exportReport.optionsType.flat_rate") },
                  { value: "KM", label: t("expenseRefundFolder.exportReport.optionsType.km") },
                  { value: "CREDIT_CARD", label: t("expenseRefundFolder.exportReport.optionsType.credit_card") },
                ]}
              />
            </Row>
          </Col>
          <Col span={8}>
            <Row justify="end">
              <Col>
                <Button
                  type="secondary"
                  currentSize="large"
                  clickCallback={resetFilter}
                  currentIcon={<ClearOutlined className="icon-large" />}
                />
              </Col>
              <Col className="left-margin">
                <Button
                  currentSize="large"
                  isLoading={filterLoading}
                  clickCallback={formikFilter.submitForm}
                  currentIcon={<SearchOutlined className="icon-large" />}
                  isDisabled={!formikFilter.isValid}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      <div className="export-buttons-container" style={{ marginBottom: "16px" }}>
        <Row gutter={16}>
          <Col>
            <Button
              type="primary"
              currentSize="large"
              isLoading={massiveExportLoading}
              clickCallback={handleMassiveExport}
              currentIcon={<CloudDownloadOutlined className="icon-large" />}
              content={t("expenseRefundFolder.exportReport.massiveExport")}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              currentSize="large"
              isLoading={downloadLoading}
              currentIcon={<DownloadOutlined className="icon-large" />}
              clickCallback={handleDownload}
            />
          </Col>
        </Row>
      </div>

      <div
        style={{
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table
          columns={columns}
          rowKey="id"
          scroll={{ x: "max-content" }}
          expandable={{
            expandedRowRender: (record) => (
              <List
                renderItem={(doc) => (
                  <Button
                    key={doc.id}
                    type="link"
                    content={t(`expenseRefundFolder.downloadAttachment.${toLower(doc.type)}`)}
                    clickCallback={() =>
                      downloadDocuments([
                        {
                          id: doc.id,
                          path: doc.filePath,
                          name: record.expenseRefundFolder.user.name,
                          surname: record.expenseRefundFolder.user.surname,
                          date: dayjs(record.docDate).format("YYYYMMDD"),
                          folderId: record.expenseRefundFolder.id,
                          noteId: record.id,
                          type: doc.type,
                          // note: record.note,
                          // expenseRefundNote: record.expenseRefundFolder.note,
                        },
                      ])
                    }
                  />
                )}
                dataSource={record.documents}
              />
            ),
          }}
          dataSource={tableData}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.size,
            total: pagination.totalItems,
            onChange: (page, pageSize) => {
              const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
              dispatch(setFilters(queryString));
              setPagination({
                ...pagination,
                currentPage: page,
                size: pageSize,
                totalItems: pagination.totalItems,
              });
              dataList(queryString.string);
            },
          }}
        />
      </div>
    </>
  );
};

export default ExportDocuments;
